import { createTheme } from "@mui/material/styles";


export const tokens = {
  color: {
    ink: "#1A1A1A", // headings, primary text, primary buttons
    text: "#333333", // body copy
    secondary: "#595959", // secondary text (7:1 on white)
    muted: "#6B6B6B", // metadata / eyebrow labels (5.3:1 on white) — replaces #999/#aaa/#bbb/#ccc
    line: "#E4E1DB", // hairlines and card borders
    ivory: "#F6F4F0", // alternate section background and image mats
    paper: "#FFFFFF",
    live: "#A61B1B", // used ONLY for "Live" indicators (6.9:1 on white)
    footerBg: "#111111",
    footerText: "#D6D6D6", // 12.9:1 on #111
    footerMuted: "#A8A8A8", // 7.6:1 on #111
  },
  font: {
    // Headings follow the León Gallery LOGO, whose lettering is closest to Bell MT (a
    // transitional serif: moderate contrast, bracketed serifs, ball terminals).
    // Bell MT has no free web licence, so the web font is Baskervville (Google Fonts), a
    // Baskerville revival — the direct model for Bell's type. "Bell MT" follows as a
    // fallback, then other Baskerville-style fonts.
    // To use the exact logo face later: license Bell MT as a web font and put it first.
    display: '"Baskervville", "Bell MT", "Libre Baskerville", Baskerville, "Baskerville Old Face", Georgia, serif',
    body: '"Inter", "Helvetica Neue", Arial, sans-serif',
  },
  layout: {
    maxWidth: 1280,
    gutter: { xs: 2, sm: 3, md: 4 }, // 16 / 24 / 32 px
    sectionY: { xs: 7, md: 12 }, // 56 / 96 px
  },
};

const { color, font } = tokens;

export const galleryTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: color.ink, contrastText: "#FFFFFF" },
    secondary: { main: color.secondary },
    error: { main: color.live },
    text: { primary: color.ink, secondary: color.secondary },
    divider: color.line,
    background: { default: color.paper, paper: color.paper },
  },
  shape: { borderRadius: 0 },
  typography: {
    fontFamily: font.body,
    htmlFontSize: 16,
    fontSize: 15,
    // Regular weight (400), like the logo. Sizes are slightly larger than a sans heading
    // because the logo-style serif has a smaller x-height.
    h1: { fontFamily: font.display, fontWeight: 400, fontSize: "2.75rem", lineHeight: 1.1 },
    h2: { fontFamily: font.display, fontWeight: 400, fontSize: "2.125rem", lineHeight: 1.18 },
    h3: { fontFamily: font.display, fontWeight: 400, fontSize: "1.375rem", lineHeight: 1.28 },
    h4: { fontFamily: font.display, fontWeight: 400, fontSize: "1.25rem", lineHeight: 1.32 },
    body1: { fontSize: "1.0625rem", lineHeight: 1.65, color: color.text }, // 17px
    body2: { fontSize: "0.9375rem", lineHeight: 1.6, color: color.secondary }, // 15px
    // Section labels echo the logo's tagline ("FINE ART & ANTIQUES"): widely spaced serif capitals.
    overline: { fontFamily: font.display, fontSize: "0.875rem", fontWeight: 400, letterSpacing: "0.18em", lineHeight: 1.5, textTransform: "uppercase", color: color.muted }, // 14px caps
    caption: { fontSize: "0.875rem", lineHeight: 1.5, color: color.muted }, // 14px
    button: { fontSize: "0.9375rem", fontWeight: 600, letterSpacing: "0.04em", textTransform: "none" },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { minHeight: 44, paddingLeft: 24, paddingRight: 24 },
        outlined: { borderColor: color.ink, "&:hover": { backgroundColor: color.ink, color: "#FFFFFF", borderColor: color.ink } },
        contained: { "&:hover": { backgroundColor: "#000000" } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 0 } } },
    MuiLink: { defaultProps: { underline: "hover", color: "inherit" } },
  },
});

galleryTheme.typography.h1 = {
  ...galleryTheme.typography.h1,
  [galleryTheme.breakpoints.up("md")]: { fontSize: "3.5rem" }, // 56px
};
galleryTheme.typography.h2 = {
  ...galleryTheme.typography.h2,
  [galleryTheme.breakpoints.up("md")]: { fontSize: "2.625rem" }, // 42px
};
galleryTheme.typography.h3 = {
  ...galleryTheme.typography.h3,
  [galleryTheme.breakpoints.up("md")]: { fontSize: "1.5rem" }, // 24px
};

export default galleryTheme;
